<template>
  <div class="dashboard">
    <div class="invalid" v-if="!loading && !validOwnershipType">
      <EmptyDisplay title="Ownership type is not defined"
                    text="Please choose either Public or Private ownership to continue.">
        <v-btn variant="flat" color="primary" :to="`/account/${account.accountID}/edit`">Fix Account</v-btn>
      </EmptyDisplay>
    </div>
    <template v-else-if="!loading && validOwnershipType">
      <DOBenchmarking v-if="selected === 'dno'" :account="account" @needs-update="refreshData">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </DOBenchmarking>

      <CyberBenchmarking v-if="selected === 'cyber'" :account="account" @needs-update="refreshData">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </CyberBenchmarking>

      <FiduciaryBenchmarking v-if="selected === 'fiduciary'" :account="account" @needs-update="refreshData">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </FiduciaryBenchmarking>

      <UmbrellaBenchmarking v-if="selected === 'umbrella'">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </UmbrellaBenchmarking>

      <EPLBenchmarking v-if="selected === 'epl'" :account="account" @needs-update="refreshData">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </EPLBenchmarking>

      <CrimeBenchmarking v-if="selected === 'crime'" :account="account" @needs-update="refreshData">
        <template #filterbar>
          <BenchmarkSelector :benchmark-options="benchmarkOptions" :selected="selected" @change="benchmarkSelectorChange" />
        </template>
      </CrimeBenchmarking>

      <div v-if="!selected" class="tw:m-auto tw:flex tw:flex-col tw:items-center tw:mt-8">

        <div class="tw:flex tw:flex-col tw:items-center">
          <h2>Account Benchmarks</h2>
          <div>Please choose from one of the benchmarks below</div>
        </div>

        <div class="tw:flex tw:gap-4 tw:mt-8 tw:w-120 tw:flex-wrap tw:justify-center">
          <template v-for="option in benchmarkOptions" v-bind:key="option.name">
            <div v-if="userMeetsBenchmarkRequirements(option)" @click="loadBenchmark(option)">
              <div class="tw:group tw:tooltip tw:pointer-events-auto tw:z-20 tw:cursor-pointer">
                <div class="tw:flex tw:h-24 tw:w-36 tw:flex-col tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-blue-200 tw:bg-white tw:shadow-md tw:transition-all tw:duration-150 tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none">
                  <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:p-2 tw:text-black-500">
                    <Network v-if="option.name === 'D&O'" class="tw:text-gray-400" />
                    <Users v-if="option.name === 'EPL'" class="tw:text-gray-400" />
                    <Umbrella v-if="option.name === 'Umbrella'" class="tw:text-gray-400" />
                    <CloudLightning v-if="option.name === 'Cyber'" class="tw:text-gray-400" />
                    <BookCheck v-if="option.name === 'Fiduciary'" class="tw:text-gray-400" />
                    <ShieldAlert v-if="option.name === 'Crime'" class="tw:text-gray-400" />

                    <h2 class="tw:text-center tw:text-sm tw:font-medium"> {{ option.name }}</h2>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>

    <PageProgress v-else/>
  </div>
</template>

<script lang="ts">
import { Account } from '@/types/Account';
import { PropType } from 'vue';
import Store from '@/store';
import DOBenchmarking from './DOBenchmarking.vue';
import CyberBenchmarking from './CyberBenchmarking.vue';
import FiduciaryBenchmarking from './FiduciaryBenchmarking.vue';
import { referenceService } from '@/services/api';
import { BenchmarkContext, BenchmarkOption, benchmarkOptions } from './Benchmarking';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import benchmarkImage from '@/assets/images/benchmarking.svg';
import BenchmarkSelector from '@/views/account/benchmarking/BenchmarkSelector.vue';
import UmbrellaBenchmarking from '@/views/account/benchmarking/UmbrellaBenchmarking.vue';
import PageProgress from '@/components/PageProgress.vue';
import EPLBenchmarking from '@/views/account/benchmarking/EPLBenchmarking.vue';
import CrimeBenchmarking from '@/views/account/benchmarking/CrimeBenchmarking.vue';
import { Umbrella, Users, Network, CloudLightning, BookCheck, ShieldAlert } from 'lucide-vue-next';

export default {
  name: 'AccountBenchmarking',
  components: {
    CrimeBenchmarking,
    EPLBenchmarking,
    Network, Users, Umbrella, CloudLightning, BookCheck, ShieldAlert,
    UmbrellaBenchmarking, BenchmarkSelector, EmptyDisplay, DOBenchmarking, CyberBenchmarking, FiduciaryBenchmarking, PageProgress },
  props: {
    account: { required: true, type: Object as PropType<Account> },
    selected: { required: false, type: String as PropType<string> },
  },
  data() {
    return {
      loading: false,
      validOwnershipType: true,
      benchmarkImage,
      benchmarkOptions,
      context: {
        injectedExposureData: [],
        injectedExposureTypes: [],
        injectedExposureCategories: {}
      } as BenchmarkContext,
    };
  },
  provide() {
    return {
      context: this.context
    };
  },
  methods: {
    benchmarkSelectorChange(newSelected: string | undefined) {
      if (!newSelected || newSelected === this.selected) {
        return;
      }
      this.$router.push(newSelected);
    },
    async refreshData(): Promise<void> {
      this.loading = true;
      const ownershipType = this.account.ownershipType?.toLowerCase();
      if (!ownershipType) {
        this.validOwnershipType = false;
        this.loading = false;
        return;
      }
      (await referenceService.exposureCategories()).filter(c => {
        const scope = c.companyScope?.toLowerCase();
        return scope === 'all' ? true : scope === ownershipType;
      }).forEach(x => this.context.injectedExposureCategories[x.label] = x.value);
      this.context.injectedExposureTypes = (await referenceService.exposureTypes()).sort((m1, m2) => m1.name.localeCompare(m2.name));
      this.context.injectedExposureData = [];
      this.loading = false;
    },
    userMeetsBenchmarkRequirements(benchmark: BenchmarkOption): boolean {
      return benchmark.requirements.every(x => x(Store.state.user));
    },
    loadBenchmark(benchmark: BenchmarkOption) {
      this.$router.push(`benchmarking/${benchmark.link}`);
    },
    async initialize() {
      await this.refreshData();
    }
  },
  async mounted() {
    await this.initialize();
  },
  watch: {
    $route() {
      this.initialize();
    }
  }
};
</script>

<style scoped>
.actions {
  margin-top: 2rem;
  display: flex;
  flex-direction: row;
  grid-column-gap: 1rem;
}
</style>
